import { fade, palette } from '../../common/colors';
import { Alert } from '../../components/Alert/Alert';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/Popover/Popover';
import { selectors } from '../../controls/shared/styles';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';

const blinkLight = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 2px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
});

const blinkDark = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 1px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
});

const waveLight = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
  '33%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 2px currentColor)',
  },
  '66%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
});

const waveDark = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '33%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 2px currentColor) drop-shadow(0 0 4px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 1px currentColor)',
  },
  '66%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 2px currentColor) drop-shadow(0 0 4px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
});

const pulseLengthMS = 300;

const waveLengthMS = 3000;
const waveSegmentMS = waveLengthMS / 3;
const WAVE_DELAYS = [0, waveSegmentMS, waveSegmentMS * 2] as const;

const LEDsSocket = styled('div', {
  display: 'flex',
  width: '$10',
  height: '$10',
  borderRadius: '99em',

  variants: {
    color: {
      attention: {
        strokeAll: fade(palette.amber600, 0.8),

        [darkThemeSelector]: {
          strokeAll: fade(palette.amber200, 0.4),
        },
      },
      negative: {
        strokeAll: fade(palette.red600, 0.8),

        [darkThemeSelector]: {
          strokeAll: fade(palette.red200, 0.4),
        },
      },
      neutral: {
        strokeAll: fade(palette.gray400, 0.8),

        [darkThemeSelector]: {
          strokeAll: fade(palette.gray200, 0.4),
        },
      },
      positive: {
        strokeAll: fade(palette.green600, 0.8),

        [darkThemeSelector]: {
          strokeAll: fade(palette.green200, 0.4),
        },
      },
    },
  },
});
LEDsSocket.displayName = 'LEDsSocket';

const LEDsLight = styled('div', {
  width: '100%',
  height: '100%',
  borderRadius: '99em',

  variants: {
    color: {
      attention: {
        backgroundColor: fade(palette.amber500, 0.9),
        color: fade(palette.amber500, 0.3),
      },
      negative: {
        backgroundColor: fade(palette.red500, 0.9),
        color: fade(palette.red500, 0.3),
      },
      neutral: {
        backgroundColor: fade(palette.white, 0.9),
        color: fade(palette.white, 0.3),
      },
      positive: {
        backgroundColor: fade(palette.green500, 0.9),
        color: fade(palette.green500, 0.3),
      },
    },
    animation: {
      none: {
        filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',

        [darkThemeSelector]: {
          filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
        },
      },
      blink: {
        animation: `${blinkLight} ${pulseLengthMS}ms step-end infinite`,
        willChange: 'opacity, transform',

        [darkThemeSelector]: {
          animationName: blinkDark,
        },
      },
      wave: {
        animation: `${waveLight} ${waveLengthMS}ms ease-in-out infinite`,
        willChange: 'opacity, transform',

        [darkThemeSelector]: {
          animationName: waveDark,
        },
      },
    },
    animationDelay: {
      0: { animationDelay: '0ms' },
      [Math.round(waveSegmentMS)]: { animationDelay: `${Math.round(waveSegmentMS)}ms` },
      [Math.round(waveSegmentMS * 2)]: { animationDelay: `${Math.round(waveSegmentMS * 2)}ms` },
    },
  },
});
LEDsLight.displayName = 'LEDsLight';

const LEDsContainer = styled('div', {
  display: 'flex',
  gap: '$3',

  variants: {
    orientation: {
      horizontal: {
        flexDirection: 'row',
      },
      vertical: {
        flexDirection: 'column',
      },
    },
  },
});
LEDsContainer.displayName = 'LEDsContainer';

export const LED_STATES = [
  'booting',
  'initializing',
  'management',
  'ip',
  'registered',
  'configured',
  'backend-lost',
  'resetting',
  'rpc',
] as const;

export type LEDsState = (typeof LED_STATES)[number];

type LEDIndex = 0 | 1 | 2;

type LEDAppearance = {
  color: 'attention' | 'negative' | 'neutral' | 'positive';
  animation: 'blink' | 'none' | 'wave';
  animationDelay?: (typeof WAVE_DELAYS)[number];
};

function LEDsLED({ color, animation, animationDelay }: LEDAppearance) {
  return (
    <LEDsSocket color={color}>
      <LEDsLight color={color} animation={animation} animationDelay={animationDelay} />
    </LEDsSocket>
  );
}

const stateConfig: Record<
  LEDsState,
  { label: string; getAnimationProps: (ledIndex: LEDIndex) => LEDAppearance }
> = {
  booting: {
    label: 'Booting',
    getAnimationProps: (ledIndex) => ({
      color: 'neutral',
      animation: 'wave',
      animationDelay: WAVE_DELAYS[ledIndex],
    }),
  },
  configured: {
    label: 'Configured',
    getAnimationProps: () => ({ color: 'neutral', animation: 'none' }),
  },
  initializing: {
    label: 'Initializing',
    getAnimationProps: (ledIndex) => ({
      color: ledIndex === 1 ? 'attention' : 'negative',
      animation: 'none',
    }),
  },
  management: {
    label: 'Management link',
    getAnimationProps: (ledIndex) => {
      let color: 'negative' | 'neutral';
      if (ledIndex === 0 || ledIndex === 2) {
        color = 'negative';
      } else {
        color = 'neutral';
      }
      return {
        color,
        animation: 'none',
      };
    },
  },
  ip: {
    label: 'IP received',
    getAnimationProps: (ledIndex) => ({
      color: ledIndex === 2 ? 'negative' : 'neutral',
      animation: 'none',
    }),
  },
  registered: {
    label: 'Registration done',
    getAnimationProps: (ledIndex) => ({
      color: ledIndex === 2 ? 'positive' : 'neutral',
      animation: 'none',
    }),
  },
  'backend-lost': {
    label: 'Backend connection lost',
    getAnimationProps: (ledIndex) => {
      let color: 'attention' | 'neutral';
      if (ledIndex === 0 || ledIndex === 1) {
        color = 'neutral';
      } else {
        color = 'attention';
      }
      return {
        color,
        animation: 'none',
      };
    },
  },
  resetting: {
    label: 'Resetting',
    getAnimationProps: () => ({
      color: 'attention',
      animation: 'blink',
    }),
  },
  rpc: {
    label: 'RPC',
    getAnimationProps: () => ({ color: 'positive', animation: 'blink' }),
  },
};

function LEDsGroup({
  orientation,
  state,
}: {
  orientation?: 'horizontal' | 'vertical';
  state: LEDsState;
}) {
  const config = stateConfig[state];

  return (
    <LEDsContainer orientation={orientation}>
      <LEDsLED {...config.getAnimationProps(0)} />
      <LEDsLED {...config.getAnimationProps(1)} />
      <LEDsLED {...config.getAnimationProps(2)} />
    </LEDsContainer>
  );
}

const LEDsGlossaryContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$10',
});
LEDsGlossaryContainer.displayName = 'LEDsGlossaryContainer';

const LEDsGlossaryPill = styled(Text, {
  background: colors.bgNeutralLight,
  padding: '$4',
  borderRadius: '$4',

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
  },
});
LEDsGlossaryPill.displayName = 'LEDsGlossaryPill';

const LEDsGlossaryLabel = styled(Text, {
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },
});
LEDsGlossaryLabel.displayName = 'LEDsGlossaryLabel';

function LEDsGlossary() {
  return (
    <LEDsGlossaryContainer>
      {LED_STATES.map((state) => (
        <AlignStack
          key={`atto-leds-glossary-${state}`}
          align="start"
          gap={8}
          preset="small"
          start={
            <LEDsGlossaryPill>
              <LEDsGroup state={state} />
            </LEDsGlossaryPill>
          }
        >
          <LEDsGlossaryLabel weight="bold">{stateConfig[state].label}</LEDsGlossaryLabel>
        </AlignStack>
      ))}
      <Alert
        type="inline"
        icon="information"
        variant="neutral"
        copy="Illustrative purposes only, doesn't reflect actual device status."
      />
    </LEDsGlossaryContainer>
  );
}

const LEDsTrigger = styled(PopoverTrigger, {
  display: 'flex',
  justifyContent: 'center',
  borderRadius: '$6',

  [selectors.focus]: {
    boxShadow: shadows.focusRingLight,
    outline: 'none',

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },
});
LEDsTrigger.displayName = 'LEDsTrigger';

export type LEDsProps = {
  orientation?: 'horizontal' | 'vertical';
  state?: LEDsState;
};

export function LEDs({ orientation = 'vertical', state = 'configured' }: LEDsProps) {
  return (
    <Popover>
      <LEDsTrigger openOnHover delay={150} closeDelay={150} aria-label="LED status reference">
        <LEDsGroup orientation={orientation} state={state} />
      </LEDsTrigger>
      <PopoverContent side="right" alignment="start">
        <LEDsGlossary />
      </PopoverContent>
    </Popover>
  );
}
